import { FormlyFieldConfig } from '@ngx-formly/core';
import { Observable } from 'rxjs';
import { FormlyFieldType } from '../model';

export const FormlyDateRangeKey = 'dateRange';

/**
 * Resolves a Formly field's current value.
 *
 * Control-backed fields (the common case) return `formControl.value`. Fields
 * declared with `fieldArray` (a Formly Repeat) get NO `formControl` registered
 * when they are rendered outside a `FieldArrayType` — ngx-formly's
 * `addFormControl` early-returns for `fieldArray` fields and leaves control
 * creation to the array component. The read-only filter panel re-types such
 * fields to a plain selection type, so their value must instead be read from the
 * model by key path (mirrors ngx-formly's internal `getFieldValue`).
 */
export const getFormlyFieldValue = (field: FormlyFieldConfig): unknown => {
  if (field.formControl) {
    return field.formControl.value;
  }

  let model = field.parent ? field.parent.model : field.model;
  const key = field.key;
  const path = Array.isArray(key) ? key : key == null ? [] : String(key).split('.');

  for (const segment of path) {
    if (model == null) {
      return model;
    }
    model = (model as Record<string, unknown>)[segment];
  }

  return model;
};

export const createFormlyDateField = (options: {
  label?: string;
  className?: string;
  defaultValue?: { startDate: string | Date; endDate?: string | Date; },
  emitDefaultValue?: boolean;
  templateOptions?: { [key: string]: any },
  props?: { [key: string]: any },
  expressions?: { [key: string]: any },
}): FormlyFieldConfig => {
  return {
    className: options.className || 'col-lg-12 col-3',
    type: FormlyFieldType.DateRange,
    key: FormlyDateRangeKey,
    defaultValue: options.defaultValue,
    props: {
      label: options.label || 'Date',
      defaultValue: options.defaultValue,
      emitDefaultValue: options.emitDefaultValue,
      ...options.templateOptions,
      ...options.props,
    },
    ...(options.expressions ? { expressions: options.expressions } : {}),
  };
};

export const extractFormlyDateRange = () => {
  return <T>(source: Observable<T>): Observable<T> => {
    return new Observable(subscriber => {
      source.subscribe({
        next: (value) => {
          const { dateRange, ...filter } = value as { dateRange: { startDate: any; endDate: any } };

          subscriber.next({
            ...filter,
            ...(dateRange?.startDate && { startDate: dateRange.startDate }),
            ...(dateRange?.endDate && { endDate: dateRange.endDate }),
          });
        },
        error: (error) => {
          subscriber.error(error);
        },
        complete: () => {
          subscriber.complete();
        }
      })
    });
  }
}
